import { useState, useEffect, useCallback } from "react";

const GUEST_NAME_KEY = "br_guest_name";
const GUEST_NAME_EVENT = "br_guest_name_set";

/** Read the stored guest name (empty string if none). */
export function getGuestName(): string {
  try {
    return localStorage.getItem(GUEST_NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Hook that re-renders when the guest name changes. */
export function useGuestName(): string {
  const [name, setName] = useState(getGuestName);

  useEffect(() => {
    const handler = () => setName(getGuestName());
    window.addEventListener(GUEST_NAME_EVENT, handler);
    return () => window.removeEventListener(GUEST_NAME_EVENT, handler);
  }, []);

  return name;
}

/**
 * Full-screen modal overlay prompting the guest for their name.
 * Only renders when no name is stored in localStorage.
 */
export function GuestNameModal() {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(() => getGuestName().length === 0);

  if (!open) return null;

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    try {
      localStorage.setItem(GUEST_NAME_KEY, trimmed);
    } catch { /* noop */ }
    window.dispatchEvent(new Event(GUEST_NAME_EVENT));
    setOpen(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
      <form
        onSubmit={handleSave}
        className="mx-4 w-full max-w-[26rem] rounded-[1.2rem] border border-[#e2e4e9] bg-white px-7 py-8 shadow-xl"
      >
        <h2 className="m-0 font-[family-name:var(--font-display)] text-[1.35rem] font-medium leading-[1.2] text-[#321b29]">
          Welcome!
        </h2>
        <p className="mt-2 text-[0.88rem] leading-relaxed text-[#70696f]">
          Enter your name so others can see who's commenting.
        </p>
        <input
          type="text"
          placeholder="Your name"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={80}
          autoFocus
          className="mt-5 w-full rounded-[0.6rem] border border-[#e2e4e9] bg-[#fafafa] px-4 py-3 text-[0.95rem] text-[#321b29] placeholder:text-[#b0a8ad] outline-none focus:border-[#762a5c]/50"
        />
        <button
          type="submit"
          disabled={!input.trim()}
          className="mt-4 w-full rounded-[0.6rem] bg-[#762a5c] py-3 text-[0.88rem] font-semibold text-white transition-colors hover:bg-[#5e2249] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Continue
        </button>
      </form>
    </div>
  );
}
